import {calculateConstructionCells} from './calculateConstructionCells';
import {calculateCitiesCache} from '../calculateCitiesCache';
import {checkIfOwner} from '../checkIfOwner';
import {checkIfSpecialBuilding} from '../../buildings/checkIfSpecialBuilding';
import {CONSTRUCTION_ACTION_TYPES} from '../../../constants/construction';
import {
  selectCurrentPlayer,
  selectHexes,
  selectPendingBuildings,
  selectSharedGameState,
  selectSpecialBuildings,
} from '../../../redux/reducers/sharedStateReducer/selectors';

const checkIfRepair = (building) => building.type === CONSTRUCTION_ACTION_TYPES.BUILDING_REPAIRMENT;

// Стор таким, каким он был бы, если бы removedBuildings не ставили вовсе, и с
// выбранным жетоном token. Постройки снимаются с конца: в ячейке с несколькими
// действиями хода остается то, что было до самого раннего из снятых
const rewindState = (state, removedBuildings, token) => {
  const removed = new Set(removedBuildings);
  const pendingBuildings = selectPendingBuildings(state);
  const hexes = {...selectHexes(state)};
  const specialBuildings = {...selectSpecialBuildings(state)};

  pendingBuildings
    .filter((building) => removed.has(building) && !checkIfRepair(building))
    .reverse()
    .forEach((building) => {
      if (checkIfSpecialBuilding(building.token)) {
        if (specialBuildings[building.hexKey] === building.token) {
          delete specialBuildings[building.hexKey];
        }
      } else if (building.previousToken) {
        hexes[building.hexKey] = building.previousToken;
      } else {
        delete hexes[building.hexKey];
      }
    });

  return {
    ...state,
    shared: {
      ...state.shared,
      hexes,
      gameState: {
        ...selectSharedGameState(state),
        pendingBuildings: pendingBuildings.filter((building) => !removed.has(building)),
        specialBuildings,
      },
    },
    private: {
      ...state.private,
      selectedEntities: {...state.private.selectedEntities, selectedToken: token},
    },
  };
};

// Постройка: ячейка доступна для ее жетона по тем же правилам, что и при клике.
// Нужна одна ячейка — и считается только она (ответ тот же, что у полного
// расчета, см. calculateConstructionCells)
const checkIfPlaceable = (state, building, removedBuildings) => {
  const {availableCells, blockedCells} = calculateConstructionCells(
    rewindState(state, removedBuildings, building.token),
    {targetHexKey: building.hexKey}
  );
  return availableCells.includes(building.hexKey) && !blockedCells.has(building.hexKey);
};

// Ремонт: чинить можно только строение своего города (как и при клике по нему),
// а дотянуть город до строения могла как раз отменяемая постройка — например,
// квартал рядом с поврежденной фермой
const checkIfRepairable = (state, repair, removedBuildings) => {
  const rewoundState = rewindState(state, removedBuildings, null);
  const citiesCache = calculateCitiesCache(selectHexes(rewoundState), selectSpecialBuildings(rewoundState));
  return checkIfOwner(repair.hexKey, selectCurrentPlayer(rewoundState).color, citiesCache);
};

const checkIfStillAllowed = (state, building, removedBuildings) => (checkIfRepair(building)
  ? checkIfRepairable(state, building, removedBuildings)
  : checkIfPlaceable(state, building, removedBuildings));

/**
 * Можно ли отменить действие строительства вне очереди (двойной клик по
 * ячейке), не сделав задним числом невозможным ни одно из более поздних
 * действий строительства этого хода.
 *
 * Кнопка отмены снимает действия строго с конца, и у нее такого вопроса нет.
 * Вне очереди — есть: вторая дорога могла лечь только потому, что первая уже
 * стояла, а проверка при завершении хода связность дорог с городом не
 * проверяет — оторванная дорога осталась бы на поле.
 *
 * Каждое более позднее действие проверяется по тем же правилам, что и при
 * клике, в том поле, какое было перед ним, — но без отменяемой постройки:
 * постройка — по доступности ячейки (calculateConstructionCells), ремонт — по
 * принадлежности строения городу игрока. Конфликт — только если без
 * отменяемой постройки действие становится недопустимым (сверка с тем же
 * полем, где она на месте, защищает от ложных отказов).
 *
 * Возвращает null, если отмена возможна, иначе {building} — действие, которое
 * нужно отменить раньше.
 */
export const findConstructionUndoConflict = (state, undoneBuilding) => {
  const pendingBuildings = selectPendingBuildings(state);
  const undoneIndex = pendingBuildings.indexOf(undoneBuilding);
  if (undoneIndex === -1) {
    return null;
  }

  for (let index = undoneIndex + 1; index < pendingBuildings.length; index++) {
    const laterBuilding = pendingBuildings[index];
    const laterAndAfter = pendingBuildings.slice(index);
    const wasAllowed = checkIfStillAllowed(state, laterBuilding, laterAndAfter);
    if (wasAllowed && !checkIfStillAllowed(state, laterBuilding, [undoneBuilding, ...laterAndAfter])) {
      return {building: laterBuilding};
    }
  }

  return null;
};
